import Badge from '@/decodo/components/atoms/badge';
import { transformCMSAssetToNextImageProps } from '@/decodo/components/atoms/Image/ImageStrapi';
import { LinkButtonProps } from '@/decodo/components/atoms/LinkButton';
import {
  LinkButtonStrapiProps,
  transformFromCMSLink,
} from '@/decodo/components/atoms/LinkButton/LinkStrapi';
import RichTextFieldStrapi from '@/decodo/components/atoms/RichTextField/RichTextFieldStrapi';
import { HomepageHeroProps } from '@/decodo/features/HomepageHero/HomepageHero';
import { HomepageHeroStrapiProps } from '@/decodo/features/HomepageHero/HomepageHeroStrapi';
import IconGoogleLetter from '~/svg/IconGoogleLetter.svg';
import { HomepageCarouselProps } from './components/HomepageCarousel';

function modifyFirstLinkProps(index: number): Partial<LinkButtonProps> {
  if (index === 0) {
    return {
      variant: 'filled',
      color: 'primary',
    };
  }

  return {
    variant: 'outlined',
    color: 'secondary',
  };
}

const extraButtons = {
  googleSignIn: (link: string, text: string): LinkButtonProps => ({
    variant: 'outlined',
    color: 'white',
    href: link,
    leftSection: <IconGoogleLetter />,
    children: text,
  }),
};

function transformButtons(
  buttons: LinkButtonStrapiProps[],
  extraButton?: {
    type: NonNullable<HomepageHeroStrapiProps['extraButton']>;
    link: NonNullable<HomepageHeroStrapiProps['extraButtonLink']>;
    text: NonNullable<HomepageHeroStrapiProps['extraButtonText']>;
  }
): LinkButtonProps[] {
  const mappedButtons = buttons.map((button, i) => ({
    ...transformFromCMSLink(button),
    ...modifyFirstLinkProps(i),
    children: button.text ?? '',
  })) as LinkButtonProps[];

  if (!extraButton) return mappedButtons;

  const renderButton = extraButtons[extraButton.type];
  if (!renderButton) return mappedButtons;

  mappedButtons.push(renderButton(extraButton.link, extraButton.text));
  return mappedButtons;
}

function transformCarousel(
  carousel: NonNullable<HomepageHeroStrapiProps['carousel']>
): HomepageCarouselProps['slides'] {
  return carousel.carouselContent.map((content) => ({
    badge: content.badgeText ? (
      <Badge
        variant='outlined'
        colors='gradient'
        classNames={{ root: 'uppercase text-base-black' }}
      >
        {content.badgeText}
      </Badge>
    ) : undefined,
    title: content.cardTitle ?? '',
    description: content.cardDescription ? (
      <RichTextFieldStrapi block={content.cardDescription} />
    ) : undefined,
    image: transformCMSAssetToNextImageProps(content.image),
  }));
}

export function transformStrapiHomepageHero(
  data: HomepageHeroStrapiProps
): HomepageHeroProps {
  const description = data.description ? (
    <RichTextFieldStrapi block={data.description} />
  ) : undefined;

  const extraButton =
    !!data.extraButton && !!data.extraButtonLink && !!data.extraButtonText
      ? {
          type: data.extraButton,
          link: data.extraButtonLink,
          text: data.extraButtonText,
        }
      : undefined;

  return {
    title: data.title ?? '',
    description: description,
    buttons: transformButtons(data.buttons ?? [], extraButton),
    showMoneyBack: data.showMoneyBack,
    features: data.featureList?.features.map((feature) => feature.name) ?? [],
    featureIcon: data.featureList?.icon
      ? transformCMSAssetToNextImageProps(data.featureList?.icon)
      : undefined,
    carousel: data.carousel ? transformCarousel(data.carousel) : [],
  };
}
